import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useState, useEffect } from 'react';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { IContribution } from '~/types/campaigns';
import { MdCheck, MdWarning, MdRestartAlt } from 'react-icons/md';
import { PiWarningDiamondFill } from 'react-icons/pi';
import moment from 'moment';
import { FiEye } from 'react-icons/fi';
import { fetchContributions } from '~/services/users';
import { useDebouncedURLSearchParams } from '~/hooks/useDebouncedUrlSearchParams';
import { Pagination } from '~/components/Pagination';
import { FaSearch } from 'react-icons/fa';

const columnHelper = createColumnHelper<IContribution & { action?: string }>();

const columns = [
    columnHelper.accessor('contributor', {
        header: () => 'Contributor',
        cell: (props) => props.renderValue(),
    }),
    columnHelper.accessor('created_date', {
        header: () => 'Date',
        cell: (props) => moment(props.getValue()).format('DD/MM/YYYY'),
    }),
    columnHelper.accessor('campaign', {
        header: () => 'Campaign',
        cell: (props) => props.renderValue(),
    }),
    columnHelper.accessor('amount', {
        header: () => 'Total',
        cell: (props) => `$ ${props.renderValue()}`,
    }),
    columnHelper.accessor('payment_status_code', {
        header: () => 'Status',
        cell: (props) => {
            const { renderValue, row } = props;
            const isValid = renderValue() === 'Valid';

            return (
                <div className='flex w-[200px] items-center gap-1'>
                    {isValid ? (
                        <div className='flex w-[200px] items-center gap-1'>
                            <div className='w-[25%] flex-shrink-0'>
                                <MdCheck
                                    className='w-full text-primary'
                                    size={30}
                                />
                            </div>
                            <div className='flex-grow'>
                                Approved
                                <div className='text-[12px]/[16px] text-[#949599]'>
                                    Trans ID: {row.original.transaction_id}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className='flex w-[200px] items-center gap-1'>
                            <div className='w-[25%] flex-shrink-0'>
                                <PiWarningDiamondFill
                                    className='m-1 w-full text-red'
                                    size={30}
                                />
                            </div>
                            <div className='flex-grow'>
                                Failed
                                <div className='text-[12px]/[16px] text-[#949599]'>
                                    {row.original.payment_status_code_text}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        },
    }),
    columnHelper.accessor('action', {
        header: () => '',
        cell: () => (
            <div className='flex justify-end gap-3'>
                <FiEye className='text-primary' size={20} />
            </div>
        ),
    }),
];

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

type DateRange = {
    start_date: string;
    end_date: string;
};

const initialDateRange = {
    start_date: '',
    end_date: '',
};

const RecurringContributions: React.FC = () => {
    const [pages, setPages] = useState<number>(1);
    const [sort, setSort] = useState<Sort | null>(null);
    const [sortBy, setSortBy] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
    const [data, setData] = useState([]);

    const [filters, setFilters, results, isLoading] =
        useDebouncedURLSearchParams(
            {
                page: 1,
            },
            'all-contributions',
            fetchContributions,
            []
        );

    useEffect(() => {
        if (results) {
            const dataFetched = results;
            setData(dataFetched.results);
            setPages(Math.ceil(dataFetched.count / 20));
        }
    }, [results, isLoading]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleSort = (columnId: string) => {
        if (sortBy === columnId) {
            setSort(sort === Sort.ASC ? Sort.DESC : Sort.ASC);
        } else {
            setSortBy(columnId);
            setSort(Sort.ASC);
        }
    };

    const handleFilterChange = (key: keyof typeof filters, value: number) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };
    return (
        <>
            <Breadcrumb pageName='Recurring Contributions' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex flex-wrap items-center justify-start gap-4 overflow-x-auto px-8 pb-4'>
                    <div className='relative min-w-[175px] max-w-[175px]'>
                        <input
                            type='text'
                            name='contributor'
                            className='w-full rounded border border-stroke p-[6px] outline-none focus:border-primary'
                            placeholder='Contributor'
                        />
                        <FaSearch
                            color='gray'
                            className='absolute right-3 top-1/2 -translate-y-1/2'
                        />
                    </div>
                    <div className='min-w-[175px] max-w-[175px]'>
                        <select
                            name='status'
                            id='status'
                            className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                        >
                            <option value='1'>Status</option>
                            <option value='2'>Second Option</option>
                        </select>
                    </div>
                    <div className='shrink-0'>Contributions from</div>
                    <input
                        type='date'
                        name='start_date'
                        className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                        value={dateRange.start_date}
                        onChange={(event) =>
                            setDateRange({
                                ...dateRange,
                                start_date: event.target.value,
                            })
                        }
                    />
                    to
                    <input
                        type='date'
                        name='end_date'
                        className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                        value={dateRange.end_date}
                        onChange={(event) =>
                            setDateRange({
                                ...dateRange,
                                end_date: event.target.value,
                            })
                        }
                    />
                </div>

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>
                        Showing {Math.min(20, (results?.count || 0) - ((filters?.page || 1) - 1) * 20)} of {results?.count || 0} results
                    </p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdCheck className='text-primary' size={18} />
                            Approved
                        </div>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdWarning className='text-danger' size={18} />
                            Failed
                        </div>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdRestartAlt size={18} />
                            Refund
                        </div>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            key={header.id}
                                        >
                                            <div className='flex items-center'>
                                                <span
                                                    onClick={() =>
                                                        handleSort(
                                                            header.column.id
                                                        )
                                                    }
                                                    className='cursor-pointer'
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column
                                                                  .columnDef
                                                                  .header,
                                                              header.getContext()
                                                          )}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                          <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                              <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                  <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => {
                                      return (
                                          <tr
                                              className={`border-t border-stroke ${
                                                  row
                                                      .getVisibleCells()
                                                      .find(
                                                          (c) =>
                                                              c.column.id ===
                                                                  'payment_status_code' &&
                                                              c.getValue() ===
                                                                  'Invalid'
                                                      )
                                                      ? 'bg-[#fdeaea] hover:bg-[#fbdada]'
                                                      : 'hover:bg-[#f5f5f5]'
                                              }`}
                                              key={row.id}
                                          >
                                              {row
                                                  .getVisibleCells()
                                                  .map((cell) => (
                                                      <td
                                                          style={{
                                                              paddingTop:
                                                                  '16px',
                                                              paddingBottom:
                                                                  '16px',
                                                          }}
                                                          key={cell.id}
                                                      >
                                                          {flexRender(
                                                              cell.column
                                                                  .columnDef
                                                                  .cell,
                                                              cell.getContext()
                                                          )}
                                                      </td>
                                                  ))}
                                          </tr>
                                      );
                                  })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={filters.page}
                    pages={pages}
                    setPage={(val: number) => handleFilterChange('page', val)}
                    totalCount={results?.count}
                />
            </section>
        </>
    );
};

export default RecurringContributions;
